import { useState } from 'react'
import { Box, Grid } from '@mui/material'
import { t } from '@lingui/macro'
import dayjs from 'dayjs'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import DateRangePickerInput from '@/components/ui/inputs/DateRangePickerInput'
import { useMonthlyReportData } from '../../model/useMonthlyReportData'
import { GeneralClinicSection } from './GeneralClinicSection'
import { MaternitySection } from './MaternitySection'

type Props = {
  establishmentId: string
  isSuperAdmin?: boolean
}

type ReportSection = 'generalClinic' | 'maternity' | false

export function MonthlyReportTab({ establishmentId }: Props) {
  const isMobile = useIsMobile()

  // Default to current month
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return [start, end]
  })

  const [expandedSection, setExpandedSection] = useState<ReportSection>('generalClinic')

  const handleAccordionChange = (panel: ReportSection) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false)
  }

  const handleRangeChange = (range: { start: Date | null; end: Date | null }) => {
    if (range.start && range.end) {
      setDateRange([range.start, range.end])
    }
  }

  const startDate = dayjs(dateRange[0]).format('YYYY-MM-DD')
  const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD')

  const reportData = useMonthlyReportData({
    establishmentId,
    startDate,
    endDate,
    isActive: true,
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <DateRangePickerInput
            value={dateRange}
            onChange={handleRangeChange}
            label={t`Report period`}
            sx={{
              width: '100%',
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '0.875rem' : undefined,
              },
            }}
          />
        </Grid>
      </Grid>

      <GeneralClinicSection
        isExpanded={expandedSection === 'generalClinic'}
        onChange={handleAccordionChange('generalClinic')}
        generalAttendance={reportData.generalAttendance}
        testsPerformed={reportData.testsPerformed}
        thresholdAlerts={reportData.thresholdAlerts}
        isMobile={isMobile}
        multiEstablishment={false}
      />

      <MaternitySection
        isExpanded={expandedSection === 'maternity'}
        onChange={handleAccordionChange('maternity')}
        maternityAttendance={reportData.maternityAttendance}
        missingExams={reportData.missingExams}
        isMobile={isMobile}
        multiEstablishment={false}
      />
    </Box>
  )
}
